import {NonEmptyString, NonNegativeInt} from '@raison/types';
import BigNumber from 'bignumber.js';
import {getUA} from 'react-device-detect';

export function getCookie(name: string, checkValue?: string) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    parts.shift();
    const values = parts.map(i => i.split(';').shift());
    if (checkValue) {
        return values.indexOf(checkValue) !== -1;
    } else if (values.length === 1) {
        return values[0];
    } else {
        return values.length ? values : null;
    }
}

/*
    right now len is always passing here as undefined,
    so it always equals 5
 */
export function fixNumber(num: number | string, len = 5, dec: number, forms?: boolean) {
    const arr = num?.toString().split('.') || [];
    let result = '';
    if (arr.length) {
        arr[0] = arr[0] === '1e-10' ? '0' : arr[0];

        len = dec ? arr[0].length + dec : len;

        arr[1] = arr[1] ? arr[1].slice(0, len - arr[0].length > 0 ? len - arr[0].length : 0) : '';

        while (arr[1] && arr[1].length > dec && arr[1].slice(-1) === '0') {
            arr[1] = arr[1].slice(0, -1);
        }

        if (!forms) {
            arr[1] = arr[1].padEnd(dec, '0');
        }

        result = arr[1] ? arr[0] + '.' + arr[1] : num?.toString().includes('.') && forms ? arr[0] + '.' : arr[0];
    } else {
        result = '';
    }
    // keep only digits and dot
    return result.replace(/[^.\d]/, '');
}

export function formatDoubleStringInput(inputValue: string, decimals: number, preserveTrailingDot?: boolean) {
    if (!inputValue) {
        return '';
    }

    const splitted = inputValue.split('.');
    const [integerPart] = splitted;
    let [, fractionalPart] = splitted;
    let result = '';

    // '123.' -> ['123', '']
    if (preserveTrailingDot && splitted.length === 2) {
        return `${integerPart || 0}.${decimals ? fractionalPart : ''}`;
    }

    if (!fractionalPart && decimals) {
        fractionalPart = '0';
    }

    if (fractionalPart) {
        if (decimals === 0) {
            return integerPart;
        }
        fractionalPart = fractionalPart.slice(0, decimals);

        if (!preserveTrailingDot) {
            fractionalPart = fractionalPart.padEnd(decimals, '0');
        }

        result = `${integerPart}.${fractionalPart}`;
    } else {
        result = integerPart;
    }

    // keep only digits and dot
    return keepDigitsAndDot(result);
}

export function keepDigitsAndDot(value: string) {
    return value.replace(/[^.\d]/g, '');
}

export type ApiClientError = {
    code: NonNegativeInt;
    message: NonEmptyString;
    errors?: {
        children: {
            [key: string]: {
                errors?: NonEmptyString[];
            };
        };
        errors?: string[];
    };
};

export function formatEur(eur: string | number) {
    const amount = new BigNumber(eur);

    const format = {
        decimalSeparator: '.',
        fractionGroupSeparator: '',
        fractionGroupSize: 0,
        groupSeparator: ' ',
        groupSize: 3,
        prefix: '',
        secondaryGroupSize: 0,
        suffix: '',
    };

    // TODO refactor with currencies abstractions in RAISON-1756
    return amount.toFormat(2, BigNumber.ROUND_HALF_UP, format);
}

/**
 *
 * @param numValue  - float number
 * @param decimals  - decimals from api for the token
 * @param eurDecimals  - decimals from api for euro
 * @return - rounded float value as a string with decimals from the argument
 */
export function roundPurchaseAmount(numValue: number, decimals: number, eurDecimals: number) {
    const converted = numValue.toString();
    const [intPart, floatPart] = converted.split('.');
    const roundedFloat = floatPart ? roundFloatPurchasePart(floatPart, decimals, eurDecimals) : '';
    const finalValue = `${intPart}.${roundedFloat}`;
    const maxLength = intPart.length + 1 + eurDecimals;
    return finalValue.padEnd(maxLength, '0');
}

/**
 * @param float  - fractional part of a float number as a string
 * @param decimals  - decimals from api for the token
 * @param eurDecimals  - decimals from api for euro
 * @return  - rounded fractional part for token purchase amount
 */
function roundFloatPurchasePart(float: string, decimals: number, eurDecimals: number) {
    if (float.length > eurDecimals) {
        const splitted = float.split('');
        const decimalPartForDisplay = splitted.slice(0, eurDecimals) as [string, string];
        const extraNumbers = float.slice(eurDecimals, eurDecimals + decimals);
        let [first, second] = decimalPartForDisplay.map(Number);
        if (parseInt(extraNumbers)) {
            if (second < 9) {
                second++;
            } else {
                first++;
                second = 0;
            }
        }
        return `${first}${second}`;
    }
    return float.slice(0, eurDecimals);
}

/**
 * @param obj - any object
 * @return returns true / false
 */

export function isEmpty(obj: object) {
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            return false;
        }
    }
    return true;
}

/**
 * @returns {boolean}
 */
export const isGonative = () => /gonative/gi.test(getUA);

/**
 * @param objOrigin {any} - any object
 * @return returns object whithout empty properties
 */

export function cleanEmptyProps<T extends object>(objOrigin: T): T {
    const obj = {...objOrigin};
    const propNames = Object.getOwnPropertyNames(obj);
    for (let i = 0; i < propNames.length; i++) {
        const propName = propNames[i] as keyof T;
        if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
            delete obj[propName];
        }
    }
    return obj;
}

/**
 * @param str - string
 * @return returns string whith first capital letter = nikolay -> Nikolay
 */

export function capitalizeFirstLetter(str: string) {
    if (!str) return str;
    const strLocal = str.toLowerCase();
    return strLocal[0].toUpperCase() + strLocal.slice(1);
}

/**
 * @param expTime - number
 * @return returns seconds amount before next attempt to resend code
 */

export function secondsAmountBeforeNextAttempt(expTime: number) {
    const dateNow = Date.now();
    const expTimeMs = expTime * 1000;

    const msAmount = Math.round((expTimeMs - dateNow) / 1000);
    return msAmount <= 0 ? 0 : msAmount;
}

/**
 * @param str - string
 * @return returns masked phoneStr like +7 965 *** ** 98
 */

export function mask5symbols(str: string) {
    if (str.length < 9) return str;
    const arr = str.split('').reverse();
    let counter = 0;

    for (let i = 0; i < arr.length; i++) {
        if (i >= 2 && counter < 5 && arr[i].match(/[0-9]/)) {
            arr[i] = '*';
            counter++;
        }
    }
    return arr.reverse().join('');
}
